import React from 'react';
import { Image, StyleSheet, Text, View, Pressable, StyleProp, ViewStyle, Dimensions, Platform, EmitterSubscription } from 'react-native';

import { apiGetProfileUri } from '../../api/users.api';
import { apiGetThotThumbnailUri } from '../../api/thots.api';

import Navigator from '../../libs/Navigator';
import convertDate from '../../libs/convertDate';

import { ThotType } from '../../models/Thot.type';
import { HomeThot } from '../../models/HomeThot';

import colorStyle from '../../styles/color.style';
import { buildThotRouteParams } from '../../libs/thotRoute';

type Props = {
  thot: HomeThot,
  style?: StyleProp<ViewStyle>,
  onRef?: Function
}
type State = {
  titleExpanded: boolean;
  titleLineCount: number;
  titleWidth: number;
  viewportVersion: number;
}

const NO_PROFILE = require('../../../assets/no_profile.png');
const CARD_WIDTH = 134;
const CARD_RADIUS = 10;
const HEADER_HEIGHT = 56;
const FOOTER_HEIGHT = 48;
const AVATAR_SIZE = 40;
const SIDE_AVATAR_SIZE = Math.round(AVATAR_SIZE * 0.85);
const AVATAR_GAP = 5;
const CENTER_LEFT = CARD_WIDTH / 2 - AVATAR_SIZE / 2;
const LEFT_AVATAR_LEFT = CENTER_LEFT - AVATAR_GAP - SIDE_AVATAR_SIZE;
const RIGHT_AVATAR_LEFT = CENTER_LEFT + AVATAR_SIZE + AVATAR_GAP;
const DEPTH_SIZE = 18;
const LEFT_DEPTH_LEFT = LEFT_AVATAR_LEFT + SIDE_AVATAR_SIZE + (AVATAR_GAP - DEPTH_SIZE) / 2;
const RIGHT_DEPTH_LEFT = CENTER_LEFT + AVATAR_SIZE + (AVATAR_GAP - DEPTH_SIZE) / 2;
const TITLE_LINE_HEIGHT = 14;
const META_LINE_HEIGHT = 10;
const TITLE_PADDING_Y = 7;
const TITLE_PADDING_X = 9;
const COMPACT_TITLE_HEIGHT = TITLE_LINE_HEIGHT + TITLE_PADDING_Y * 2;

const getImageHeight = () => {
  const screen = Dimensions.get('window');
  return Math.round(CARD_WIDTH * ((screen.height - HEADER_HEIGHT) / Math.max(screen.width, 1)));
};

const getMediaHeight = () => COMPACT_TITLE_HEIGHT + getImageHeight();
const getCardHeight = () => getMediaHeight() + FOOTER_HEIGHT;

export default class AnimatedThot extends React.Component<Props, State> {
  _dimensionsSubscription?: EmitterSubscription;

  state: State = {
    titleExpanded: false,
    titleLineCount: 1,
    titleWidth: 0,
    viewportVersion: 0
  };

  componentDidMount() {
    this._dimensionsSubscription = Dimensions.addEventListener('change', this._onDimensionsChange);
  }

  componentWillUnmount() {
    this._dimensionsSubscription?.remove();
  }

  _onDimensionsChange = () => {
    this.setState(({ viewportVersion }) => ({ viewportVersion: viewportVersion + 1 }));
  }

  _setRef = (ref: View) => {
    if (this.props.onRef) this.props.onRef(ref, this.props.thot.current._id);
  }

  _onPressThot = _ => this._goTo(this.props.thot.current);
  _onPressRoot = _ => this._goTo(this.props.thot.root);
  _onPressLast = _ => this._goTo(this.props.thot.last);

  _goTo = (thot?: ThotType) => {
    if(thot) Navigator.navigate('Thot', buildThotRouteParams(thot));
  }

  _getUserId = (thot: ThotType): string => {
    if (!thot) return undefined;
    return typeof (thot.user) == 'string' ? thot.user : thot.user._id;
  }

  _compareDepth = (t1: ThotType, t2: ThotType) => {
    if (!t1 || !t2 || t1.depth == undefined || t2.depth == undefined) return undefined;
    const d = (t2.depth - t1.depth - 1);
    return d > 0 ? d : undefined;
  }

  _setTitleExpanded = (titleExpanded: boolean) => {
    if (this.state.titleExpanded === titleExpanded) return;
    this.setState({ titleExpanded });
  }

  _measureTitleLines = (titleWidth: number) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined' || !titleWidth) return;

    const probe = document.createElement('div');
    probe.textContent = this.props.thot.current.title;
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    probe.style.pointerEvents = 'none';
    probe.style.left = '-99999px';
    probe.style.top = '0';
    probe.style.width = `${Math.max(titleWidth - TITLE_PADDING_X * 2, 0)}px`;
    probe.style.whiteSpace = 'normal';
    probe.style.wordBreak = 'break-word';
    probe.style.fontSize = '11.5px';
    probe.style.fontWeight = '600';
    probe.style.lineHeight = `${TITLE_LINE_HEIGHT}px`;
    probe.style.textAlign = 'center';
    document.body.appendChild(probe);
    const titleLineCount = Math.max(1, Math.round(probe.scrollHeight / TITLE_LINE_HEIGHT));
    document.body.removeChild(probe);

    if (titleLineCount === this.state.titleLineCount) return;
    this.setState({ titleLineCount });
  }

  _onTitleLayout = (evt) => {
    const titleWidth = Math.round(evt?.nativeEvent?.layout?.width || 0);
    if (!titleWidth || titleWidth === this.state.titleWidth) return;

    this.setState({ titleWidth }, () => {
      if (Platform.OS === 'web') this._measureTitleLines(titleWidth);
    });
  }

  _onTitleTextLayout = (evt) => {
    if (Platform.OS === 'web') return;
    const titleLineCount = evt?.nativeEvent?.lines?.length || 1;
    if (titleLineCount === this.state.titleLineCount) return;
    this.setState({ titleLineCount });
  }

  render() {
    const { thot: { root, current, last, user }, style } = this.props;
    const imageHeight = getImageHeight();
    const mediaHeight = getMediaHeight();
    const cardHeight = getCardHeight();
    const rootUser = root && typeof root.user == 'string' ? root.user : undefined;
    const lastUser = last && typeof last.user == 'string' ? last.user : undefined;
    const rootDepth = this._compareDepth( root, current);
    const lastDepth = this._compareDepth( current, last);
    const metaVisible = this.state.titleExpanded;
    const avatarOverlayVisible = this.state.titleExpanded;
    const maxExpandedLines = Math.max(2, Math.floor((mediaHeight - TITLE_PADDING_Y * 2 - META_LINE_HEIGHT - 4) / TITLE_LINE_HEIGHT));
    const visibleLineCount = Math.min(this.state.titleExpanded ? maxExpandedLines : 1, this.state.titleLineCount || 1);
    const titleBubbleHeight = visibleLineCount * TITLE_LINE_HEIGHT + TITLE_PADDING_Y * 2 + (metaVisible ? META_LINE_HEIGHT + 3 : 0);

    return (
      <View ref={this._setRef} style={[styles.thot, { height: cardHeight }, style]}>
        <Pressable
          style={[styles.mediaPressable, { height: mediaHeight }]}
          onPress={this._onPressThot}
          onHoverIn={() => this._setTitleExpanded(true)}
          onHoverOut={() => this._setTitleExpanded(false)}
          onPressIn={() => this._setTitleExpanded(true)}
          onPressOut={() => this._setTitleExpanded(false)}
          onLongPress={() => {}}
          onContextMenu={Platform.OS === 'web' ? ((evt) => evt.preventDefault()) : undefined}
        >
          <Image source={{uri: apiGetThotThumbnailUri(current._id)}} style={[styles.video, { height: imageHeight }]} />
          <View style={styles.titleMeasure} onLayout={this._onTitleLayout}>
            <Text style={styles.titleMeasureText} onTextLayout={this._onTitleTextLayout}>{current.title}</Text>
          </View>
          <View style={[styles.titleBubble, { height: titleBubbleHeight }]}>
            <Text style={styles.title} numberOfLines={this.state.titleExpanded ? maxExpandedLines : 1} ellipsizeMode="tail">
              {current.title}
            </Text>
            {metaVisible && (
              <Text style={styles.meta} numberOfLines={1} ellipsizeMode="tail">
                @{user.name} <Text style={styles.date}>{convertDate(current.created_at)}</Text>
              </Text>
            )}
          </View>
        </Pressable>
          {rootUser &&
            <Pressable style={[styles.sideImageWrap, styles.leftImageWrap, styles.avatarOverlayItem, !avatarOverlayVisible && styles.avatarOverlayItemHidden, { top: mediaHeight - SIDE_AVATAR_SIZE - 5 }]} onPress={this._onPressRoot} >
              <Image style={styles.sideImage} source={{ uri: apiGetProfileUri(rootUser) }} defaultSource={NO_PROFILE} />
            </Pressable>
            ||
            <View style={[styles.sideImageWrap, styles.leftImageWrap, styles.avatarOverlayItem, styles.avatarOverlayItemHidden, { top: mediaHeight - SIDE_AVATAR_SIZE - 5 }]} />
          }

          {rootUser && (
            <View style={[styles.depthGap, styles.leftDepthGap, styles.avatarOverlayItem, !avatarOverlayVisible && styles.avatarOverlayItemHidden, { top: mediaHeight - DEPTH_SIZE - 5 }]}>
              <View style={styles.depth}>
                <Text style={[styles.depthText, !(rootDepth > 0) && styles.depthTextZero]} >{rootDepth || 0}</Text>
              </View>
            </View>
          )}

          <Pressable style={[styles.centerImageWrap, styles.avatarOverlayItem, !avatarOverlayVisible && styles.avatarOverlayItemHidden, { top: mediaHeight - AVATAR_SIZE - 5 }]} onPress={this._onPressThot} >
            <Image style={styles.centerImage} source={{ uri: apiGetProfileUri(user._id) }} defaultSource={NO_PROFILE} />
          </Pressable>

          {lastUser && (
            <View style={[styles.depthGap, styles.rightDepthGap, styles.avatarOverlayItem, !avatarOverlayVisible && styles.avatarOverlayItemHidden, { top: mediaHeight - DEPTH_SIZE - 5 }]}>
              <View style={styles.depth}>
                <Text style={[styles.depthText, !(lastDepth > 0) && styles.depthTextZero]} >{lastDepth || 0}</Text>
              </View>
            </View>
          )}

          {lastUser &&
            <Pressable style={[styles.sideImageWrap, styles.rightImageWrap, styles.avatarOverlayItem, !avatarOverlayVisible && styles.avatarOverlayItemHidden, { top: mediaHeight - SIDE_AVATAR_SIZE - 5 }]} onPress={this._onPressLast} >
              <Image style={styles.sideImage} source={{ uri: apiGetProfileUri(lastUser) }} defaultSource={NO_PROFILE} />
            </Pressable>
            ||
            <View style={[styles.sideImageWrap, styles.rightImageWrap, styles.avatarOverlayItem, styles.avatarOverlayItemHidden, { top: mediaHeight - SIDE_AVATAR_SIZE - 5 }]} />
          }

      </View>
    );
  }
}

const styles = StyleSheet.create({
  thot: {
    width: CARD_WIDTH,
    marginRight: 20,
    backgroundColor: 'transparent',
    overflow: 'visible'
  },
  mediaPressable: {
    position: 'relative',
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'hidden',
    borderRadius: CARD_RADIUS,
    ...(Platform.OS === 'web' ? ({ WebkitTouchCallout: 'none' } as any) : {})
  },
  video: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: COMPACT_TITLE_HEIGHT,
    bottom: 0,
    resizeMode: 'cover',
    borderRadius: CARD_RADIUS
  },
  titleMeasure: {
    position: 'absolute',
    left: TITLE_PADDING_X,
    right: TITLE_PADDING_X,
    top: TITLE_PADDING_Y,
    opacity: 0,
    pointerEvents: 'none'
  },
  titleMeasureText: {
    fontSize: 11.5,
    lineHeight: TITLE_LINE_HEIGHT,
    fontWeight: '600',
    textAlign: 'center'
  },
  titleBubble: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    paddingHorizontal: TITLE_PADDING_X,
    paddingVertical: TITLE_PADDING_Y,
    backgroundColor: 'rgba(237,235,234,0.96)',
    overflow: 'hidden',
    borderTopLeftRadius: CARD_RADIUS,
    borderTopRightRadius: CARD_RADIUS,
    ...(Platform.OS === 'web'
      ? ({
          transitionProperty: 'height',
          transitionDuration: '280ms',
          transitionTimingFunction: 'ease-in-out'
        } as any)
      : {})
  },
  title: {
    textAlign: 'center',
    fontSize: 11.5,
    lineHeight: TITLE_LINE_HEIGHT,
    fontWeight: '600'
  },
  meta: {
    marginTop: 3,
    fontSize: 9.5,
    lineHeight: META_LINE_HEIGHT,
    textAlign: 'center'
  },
  sideImageWrap: {
    position: 'absolute',
    width: SIDE_AVATAR_SIZE,
    height: SIDE_AVATAR_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerImageWrap: {
    position: 'absolute',
    left: CENTER_LEFT,
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideImage: {
    width: SIDE_AVATAR_SIZE,
    height: SIDE_AVATAR_SIZE,
    borderRadius: SIDE_AVATAR_SIZE / 2
  },
  centerImage: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2
  },
  avatarOverlayItem: {
    opacity: 1,
    transform: [{ scale: 1 }],
    ...(Platform.OS === 'web'
      ? ({
          transitionProperty: 'opacity, transform',
          transitionDuration: '220ms',
          transitionTimingFunction: 'ease-in-out'
        } as any)
      : {})
  },
  avatarOverlayItemHidden: {
    opacity: 0,
    transform: [{ scale: 0.6 }],
    pointerEvents: 'none'
  },
  date: {
    color: 'rgba(0, 0, 0, 0.5)'
  },
  depthGap: {
    position: 'absolute',
    width: DEPTH_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    height: DEPTH_SIZE,
  },
  leftImageWrap: {
    left: LEFT_AVATAR_LEFT,
  },
  rightImageWrap: {
    left: RIGHT_AVATAR_LEFT,
  },
  leftDepthGap: {
    left: LEFT_DEPTH_LEFT,
  },
  rightDepthGap: {
    left: RIGHT_DEPTH_LEFT,
  },
  depth: {
    minWidth: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 1,
    paddingHorizontal: 5,
    backgroundColor: 'black',
    borderRadius: 8
  },
  depthText: {
    fontSize: 10,
    color: 'white'
  },
  depthTextZero: {
    color: 'black'
  }
})
